import { useEffect, useState } from 'react'
import { documentsApi, type DocumentReadField, type DocumentReadProposal, type DocumentReadKind } from '../api/documents'
import { Button, Callout, Dialog, FormGrid, IconButton, LoadingState, Select, StatusPill, TextField } from './ui'

const KINDS: { value: DocumentReadKind; label: string }[] = [
  { value: 'carta_identita', label: "Carta d'identità" },
  { value: 'bolletta', label: 'Bolletta' },
  { value: 'visura', label: 'Visura camerale' },
]

const FIELD_LABELS: Record<string, string> = {
  nome: 'Nome', cognome: 'Cognome', codiceFiscale: 'Codice fiscale', dataNascita: 'Data di nascita',
  luogoNascita: 'Luogo di nascita', numeroDocumento: 'Numero documento', scadenza: 'Scadenza', indirizzo: 'Indirizzo',
  intestatario: 'Intestatario', indirizzoIntestatario: 'Indirizzo di intestazione', indirizzoFornitura: 'Indirizzo di fornitura', fornitore: 'Fornitore', pod: 'POD', pdr: 'PDR',
  numeroFattura: 'Numero fattura', importo: 'Importo', dataEmissione: 'Data emissione',
  potenzaImpegnata: 'Potenza impegnata (kW)', potenzaDisponibile: 'Potenza disponibile (kW)', consumoAnnuo: 'Consumo annuo luce (kWh)',
  consumoAnnuoGas: 'Consumo annuo gas (Smc)', tensione: 'Tensione (BT/MT)', distributore: 'Distributore di rete',
  ragioneSociale: 'Ragione sociale', partitaIva: 'Partita IVA', sedeLegale: 'Sede legale', formaGiuridica: 'Forma giuridica',
  pec: 'PEC', codiceAteco: 'Codice ATECO',
}

const label = (name: string) => FIELD_LABELS[name] ?? name

/**
 * Lettura con IA: Claude propone i dati del documento, l'operatore li corregge e li conferma.
 * Niente viene scritto sul cliente o sulla pratica: i valori confermati si copiano dove servono.
 */
export function DocumentAiReadDialog({ documentId, documentName, onClose }: {
  documentId: number | null
  documentName: string
  onClose: () => void
}) {
  const [kind, setKind] = useState<DocumentReadKind>('carta_identita')
  const [reading, setReading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<DocumentReadField[] | null>(null)
  const [source, setSource] = useState<DocumentReadProposal['source']>()
  const [values, setValues] = useState<Record<string, string>>({})
  const [confirmed, setConfirmed] = useState<Record<string, string> | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setFields(null); setValues({}); setConfirmed(null); setError(null)
  }, [documentId])

  const read = async () => {
    if (documentId === null) return
    setReading(true); setError(null); setFields(null); setConfirmed(null)
    try {
      const proposal = await documentsApi.readProposal(documentId, kind)
      setFields(proposal.fields)
      setSource(proposal.source)
      setValues(Object.fromEntries(proposal.fields.map(f => [f.name, f.value])))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Lettura non riuscita.')
    } finally {
      setReading(false)
    }
  }

  const confirm = async () => {
    if (documentId === null) return
    setSaving(true); setError(null)
    try {
      const kept = Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== '').map(([k, v]) => [k, v.trim()]))
      const res = await documentsApi.readConfirm(documentId, kind, kept)
      setConfirmed(res.fields)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Conferma non riuscita.')
    } finally {
      setSaving(false)
    }
  }

  const copy = (text: string) => { void navigator.clipboard?.writeText(text) }

  return (
    <Dialog
      open={documentId !== null}
      onClose={onClose}
      title="Leggi con IA"
      icon="document_scanner"
      size="lg"
      supportingText={`${documentName}: l'IA propone i dati, tu li controlli prima di usarli.`}
      actions={
        <>
          <Button variant="text" onClick={onClose}>Chiudi</Button>
          {fields && fields.length > 0 && !confirmed && (
            <Button variant="filled" onClick={() => void confirm()} disabled={saving}>
              {saving ? 'Conferma…' : 'Conferma i dati'}
            </Button>
          )}
        </>
      }
    >
      <FormGrid>
        <Select label="Tipo di documento" value={kind} disabled={reading || saving}
          onChange={e => { setKind(e.target.value as DocumentReadKind); setFields(null); setConfirmed(null) }}>
          {KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
        </Select>
        <div className="d-flex align-items-center">
          <Button variant="tonal" onClick={() => void read()} disabled={reading || saving}>
            {fields ? 'Rileggi' : 'Leggi il documento'}
          </Button>
        </div>
      </FormGrid>

      {reading && <LoadingState />}
      {error && <Callout tone="error" className="mt-3">{error}</Callout>}

      {fields && fields.length === 0 && (
        <Callout tone="warning" className="mt-3">L'IA non ha trovato dati per questo tipo di documento.</Callout>
      )}

      {fields && fields.length > 0 && !confirmed && source === 'modello' && (
        <Callout tone="info" className="mt-3">Letta con il modello di questo fornitore: nessun costo IA.</Callout>
      )}

      {fields && fields.length > 0 && !confirmed && (
        <FormGrid className="mt-3">
          {fields.map(f => (
            <TextField key={f.name} label={label(f.name)} value={values[f.name] ?? ''}
              onChange={e => setValues(p => ({ ...p, [f.name]: e.target.value }))}
              supportingText={
                <StatusPill tone={f.confidence >= 0.8 ? 'success' : f.confidence >= 0.5 ? 'warning' : 'error'}>
                  Sicurezza {Math.round(f.confidence * 100)}%
                </StatusPill>
              } />
          ))}
        </FormGrid>
      )}

      {confirmed && (
        <>
          <Callout tone="success" className="mt-3">Dati confermati. Copiali nella scheda del cliente o della pratica.</Callout>
          <dl className="mt-3">
            {Object.entries(confirmed).map(([k, v]) => (
              <div key={k} className="d-flex align-items-center gap-2 py-1">
                <dt className="text-muted md-typescale-body-small" style={{ minWidth: '12rem' }}>{label(k)}</dt>
                <dd className="mb-0 flex-grow-1">{v}</dd>
                <IconButton icon="content_copy" size="sm" aria-label={`Copia ${label(k)}`} onClick={() => copy(v)} />
              </div>
            ))}
          </dl>
        </>
      )}
    </Dialog>
  )
}
